import { gql } from '@apollo/client';
import { CropColorFragment, NoteFragment } from './queries';
import defaultFarmFields from './constants/defaultFarmFields';

export const UpdateFarmMutation = gql`
mutation UpdateFarm($input: UpdateFarmInput!) {
  updateFarm(input: $input) {
    result {
      id
      updatedAt
      objectUpdatedAt
      owner {
          id
          email
      }
      team {
        id
        name
      }
      ${defaultFarmFields.join(' ')}
    }
    errors
  }
}
`;

// Arguments are pulled out individually to avoid having to clear out
// irrelevant fields that are stored in a section clientside.
export const UpsertSectionMutation = gql`
  mutation UpsertSection(
    $id: ID!
    $farmId: ID!
    $updatedAt: String
    $sectionCropMetaVersion: Int!
    $cropId: ID
    $fieldFeature: JSON
    $rowFeature: JSON
    $inRowSpacing: Float
    $betweenRowSpacing: Float
    $name: String!
    $headlandWidth: Float
    $headlandInnerFeature: Boolean
    $rotation: Float
    $offsetX: Float
    $offsetY: Float
    $legacyGridOffset: Boolean
    $rowOrderIsReversed: Boolean
    $orderWithinRowsIsReversed: Boolean
    $rotationCenter: JSON
    $rowOverrides: String!
    $s3VersionId: String
    $type: String!
    $areaFraction: Float
    $cropMetas: [SectionCropMetaAttributes!]!
    $startYear: Int
    $primaryCropVarietyId: ID
    $primaryInRowPatternId: ID
  ) {
    upsertSection(
      input: {
        id: $id
        farmId: $farmId
        updatedAt: $updatedAt
        sectionCropMetaVersion: $sectionCropMetaVersion
        cropId: $cropId
        fieldFeature: $fieldFeature
        rowFeature: $rowFeature
        inRowSpacing: $inRowSpacing
        betweenRowSpacing: $betweenRowSpacing
        name: $name
        headlandWidth: $headlandWidth
        headlandInnerFeature: $headlandInnerFeature
        rotation: $rotation
        offsetX: $offsetX
        offsetY: $offsetY
        legacyGridOffset: $legacyGridOffset
        rowOrderIsReversed: $rowOrderIsReversed
        orderWithinRowsIsReversed: $orderWithinRowsIsReversed
        rotationCenter: $rotationCenter
        rowOverrides: $rowOverrides
        s3VersionId: $s3VersionId
        type: $type
        areaFraction: $areaFraction
        cropMetas: $cropMetas
        startYear: $startYear
        primaryCropVarietyId: $primaryCropVarietyId
        primaryInRowPatternId: $primaryInRowPatternId
      }
    ) {
      errors
      result {
        id
        updatedAt
      }
    }
  }
`;

export const DeleteSectionMutation = gql`
  mutation DeleteSection($input: DeleteSectionInput!) {
    deleteSection(input: $input) {
      result
    }
  }
`;

export const UpsertLayer = gql`
  mutation UpsertLayer($input: UpsertLayerInput!) {
    upsertLayer(input: $input) {
      result {
        id
        layerType
        displayName
        updatedAt
      }
      errors
    }
  }
`;

export const DeleteLayer = gql`
  mutation DeleteLayer($input: DeleteLayerInput!) {
    deleteLayer(input: $input) {
      result
      errors
    }
  }
`;

export const CreateLayerFeature = gql`
  mutation CreateLayerFeature(
    $id: ID!
    $farmId: ID!
    $layerId: ID!
    $layerS3VersionId: String!
  ) {
    createLayerFeature(
      input: {
        id: $id
        farmId: $farmId
        layerId: $layerId
        layerS3VersionId: $layerS3VersionId
      }
    ) {
      result {
        updatedAt
        layerFeature(id: $id) {
          updatedAt
        }
      }
      errors
    }
  }
`;

export const BulkCreateLayerFeatures = gql`
  mutation BulkCreateLayerFeatures(
    $input: BulkCreateLayerFeaturesMutationInput!
  ) {
    bulkCreateLayerFeatures(input: $input) {
      result {
        updatedAt
        layerFeatures {
          id
          updatedAt
        }
      }
      errors
    }
  }
`;

export const DeleteLayerFeature = gql`
  mutation DeleteLayerFeature($input: DeleteLayerFeatureInput!) {
    deleteLayerFeature(input: $input) {
      result {
        updatedAt
      }
      errors
    }
  }
`;

export const DuplicateFarmMutation = gql`
  mutation DuplicateFarm($farmId: ID!) {
    duplicateFarm(input: { farmId: $farmId }) {
      result {
        id
        name
      }
      errors
    }
  }
`;

export const ArchiveFarmMutation = gql`
  mutation ArchiveFarm($input: ArchiveFarmInput!) {
    archiveFarm(input: $input) {
      result {
        id
        isArchived
      }
    }
  }
`;

export const UnarchiveFarmMutation = gql`
  mutation UnarchiveFarm($input: UnarchiveFarmInput!) {
    unarchiveFarm(input: $input) {
      result {
        id
        isArchived
      }
    }
  }
`;

export const LockFarmMutation = gql`
  mutation LockFarm($input: LockFarmInput!) {
    lockFarm(input: $input) {
      result {
        id
        isLocked
      }
    }
  }
`;

export const UnlockFarmMutation = gql`
  mutation UnlockFarm($input: UnlockFarmInput!) {
    unlockFarm(input: $input) {
      result {
        id
        isLocked
      }
    }
  }
`;

export const DeleteFarmMutation = gql`
  mutation DeleteFarm($input: DeleteFarmInput!) {
    deleteFarm(input: $input) {
      result
    }
  }
`;

export const UpdateCropMutation = gql`
  mutation UpdateCrop($id: ID!, $colorId: String) {
    updateCrop(input: { id: $id, colorId: $colorId }) {
      result {
        id
        updatedAt
      }
      errors
    }
  }
`;

export const AddCropToFarmMutation = gql`
  ${CropColorFragment}
  mutation AddCropToFarm($id: ID!, $farmId: ID!) {
    addCrop(input: { id: $id, farmId: $farmId }) {
      result {
        id
        name
        description
        template {
          id
          name
          team {
            id
          }
        }
        inRowSpacing
        betweenRowSpacing
        color {
          ...CropColorFields
        }
        updatedAt
      }
      errors
    }
  }
`;

export const DuplicateCropTemplateMutation = gql`
  mutation DuplicateCropTemplate($id: ID!) {
    duplicateCropTemplate(input: { id: $id }) {
      result {
        id
      }
      errors
    }
  }
`;

export const RevertToCropTemplateMutation = gql`
  mutation RevertToCropTemplate($id: ID!) {
    revertToCropTemplate(input: { id: $id }) {
      result {
        id
        name
      }
      errors
    }
  }
`;

export const CreateTemplateFromCrop = gql`
  mutation CreateTemplateFromCrop($input: CreateTemplateFromCropInput!) {
    createTemplateFromCrop(input: $input) {
      result {
        id
        team {
          id
        }
      }
      errors
    }
  }
`;

export const DuplicateCropMutation = gql`
  mutation DuplicateCrop($input: DuplicateCropInput!) {
    duplicateCrop(input: $input) {
      errors
      result {
        id
      }
    }
  }
`;

export const UpsertCropTemplateMutation = gql`
  mutation UpsertCropTemplate($input: UpsertCropTemplateInput!) {
    upsertCropTemplate(input: $input) {
      errors
      result {
        id
      }
    }
  }
`;

export const CreateFarmCropMutation = gql`
  ${CropColorFragment}
  mutation CreateFarmCrop($input: UpsertFarmCropInput!) {
    upsertFarmCrop(input: $input) {
      errors
      result {
        id
        name
        description
        template {
          id
          name
          team {
            id
          }
        }
        inRowSpacing
        betweenRowSpacing
        color {
          ...CropColorFields
        }
        updatedAt
      }
    }
  }
`;

export const UpsertFarmCropMutation = gql`
  mutation UpsertFarmCrop($input: UpsertFarmCropInput!) {
    upsertFarmCrop(input: $input) {
      errors
      result {
        id
        updatedAt
      }
    }
  }
`;

export const DeleteCropTemplateMutation = gql`
  mutation DeleteCropTemplate($input: DeleteCropTemplateInput!) {
    deleteCropTemplate(input: $input) {
      result
    }
  }
`;

export const DeleteFarmCropMutation = gql`
  mutation DeleteFarmCrop($input: DeleteFarmCropInput!) {
    deleteFarmCrop(input: $input) {
      result
    }
  }
`;

export const DeleteTemplateOperationMutation = gql`
  mutation DeleteTemplateOperation($input: DeleteCropTemplateOperationInput!) {
    deleteCropTemplateOperation(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteFarmCropOperationMutation = gql`
  mutation DeleteFarmCropOperation($input: DeleteCropOperationInput!) {
    deleteFarmCropOperation(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteTemplateLossEventMutation = gql`
  mutation DeleteTemplateLossEvent($input: DeleteCropTemplateLossEventInput!) {
    deleteCropTemplateLossEvent(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteFarmCropLossEventMutation = gql`
  mutation DeleteFarmCropLossEvent($input: DeleteCropLossEventInput!) {
    deleteFarmCropLossEvent(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const CreateInputResourceMutation = gql`
  mutation CreateInputResource($input: CreateInputResourceInput!) {
    createInputResource(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdateInputResourceMutation = gql`
  mutation UpdateInputResource($input: UpdateInputResourceInput!) {
    updateInputResource(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteInputResourceMutation = gql`
  mutation DeleteInputResource($input: DeleteInputResourceInput!) {
    deleteInputResource(input: $input) {
      result
    }
  }
`;

export const CreateInputMutation = gql`
  mutation CreateInput($input: CreateInputInput!) {
    createInput(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdateInputMutation = gql`
  mutation UpdateInput($input: UpdateInputInput!) {
    updateInput(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteInputMutation = gql`
  mutation DeleteInput($input: DeleteInputInput!) {
    deleteInput(input: $input) {
      result
    }
  }
`;

export const CreatePricingResourceMutation = gql`
  mutation CreatePricingResource($input: CreatePricingResourceInput!) {
    createPricingResource(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdatePricingResourceMutation = gql`
  mutation UpdatePricingResource($input: UpdatePricingResourceInput!) {
    updatePricingResource(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeletePricingResourceMutation = gql`
  mutation DeletePricingResource($input: DeletePricingResourceInput!) {
    deletePricingResource(input: $input) {
      result
    }
  }
`;

export const CreatePricingMutation = gql`
  mutation CreatePricing($input: CreatePricingInput!) {
    createPricing(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdatePricingMutation = gql`
  mutation UpdatePricing($input: UpdatePricingInput!) {
    updatePricing(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeletePricingMutation = gql`
  mutation DeletePricing($input: DeletePricingInput!) {
    deletePricing(input: $input) {
      result
    }
  }
`;

export const CreateCapexResourceMutation = gql`
  mutation CreateCapexResource($input: CreateCapexResourceInput!) {
    createCapexResource(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdateCapexTemplatesMutation = gql`
  mutation UpdateCapexTemplates($input: UpdateCapexTemplatesInput!) {
    updateCapexTemplates(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteCapexTemplatesMutation = gql`
  mutation DeleteCapexTemplates($input: DeleteCapexTemplatesInput!) {
    deleteCapexTemplates(input: $input) {
      result
      errors
    }
  }
`;

export const CreateCapexMutation = gql`
  mutation CreateCapex($input: CreateCapexInput!) {
    createCapex(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpdateCapexesMutation = gql`
  mutation UpdateCapexes($input: UpdateCapexesInput!) {
    updateCapexes(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const DeleteCapexesMutation = gql`
  mutation DeleteCapexes($input: DeleteCapexesInput!) {
    deleteCapexes(input: $input) {
      result
      errors
    }
  }
`;

export const UpsertTeamMutation = gql`
  mutation UpsertTeam($input: UpsertTeamInput!) {
    upsertTeam(input: $input) {
      result {
        id
        name
        updatedAt
        signedUrl
      }
      errors
    }
  }
`;

export const UpsertTeamMembersMutation = gql`
  mutation UpsertTeamMembersMutation($input: UpsertTeamMembersInput!) {
    upsertTeamMembers(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const UpsertFarmMembersMutation = gql`
  mutation UpsertFarmMembersMutation($input: UpsertFarmMembersInput!) {
    upsertFarmMembers(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const RemoveFarmMemberMutation = gql`
  mutation RemoveFarmMemberMutation($input: RemoveFarmMemberInput!) {
    removeFarmMember(input: $input) {
      result {
        id
      }
      errors
    }
  }
`;

export const ShareFarmMutation = gql`
  mutation ShareFarmMutation($input: UpdateFarmIsSharedInput!) {
    updateFarmIsShared(input: $input) {
      result {
        id
        updatedAt
      }
      errors
    }
  }
`;

export const DeleteSectionGroupMutation = gql`
  mutation DeleteSectionGroup($input: DeleteSectionGroupInput!) {
    deleteSectionGroup(input: $input) {
      result
      errors
    }
  }
`;

export const UpsertSectionGroupMutation = gql`
  mutation UpsertSectionGroup($input: UpsertSectionGroupInput!) {
    upsertSectionGroup(input: $input) {
      result {
        id
        name
        sections {
          id
        }
        updatedAt
      }
      errors
    }
  }
`;

export const CreateFarmInRowPatternMutation = gql`
  mutation CreateFarmInRowPattern($input: CreateFarmInRowPatternInput!) {
    createFarmInRowPattern(input: $input) {
      result {
        id
        updatedAt
      }
      errors
    }
  }
`;

export const UpdateFarmInRowPatternMutation = gql`
  mutation UpdateFarmInRowPattern($input: UpdateFarmInRowPatternInput!) {
    updateFarmInRowPattern(input: $input) {
      result {
        id
        updatedAt
        description
        name
      }
      errors
    }
  }
`;

export const DeleteFarmInRowPatternMutation = gql`
  mutation DeleteFarmInRowPattern($input: DeleteFarmInRowPatternInput!) {
    deleteFarmInRowPattern(input: $input) {
      result
    }
  }
`;

export const UpsertNoteMutation = gql`
  ${NoteFragment}
  mutation UpsertNoteMutation($input: UpsertNoteInput!) {
    upsertNote(input: $input) {
      result {
        ...NoteFields
      }
      errors
    }
  }
`;

export const DeleteNoteMutation = gql`
  mutation DeleteNoteMutation($input: DeleteNoteInput!) {
    deleteNote(input: $input) {
      result
      errors
    }
  }
`;

export const UpsertMultiProjectDashboardMutation = gql`
  mutation UpsertMultiProjectDashboard(
    $input: UpsertMultiProjectDashboardInput!
  ) {
    upsertMultiProjectDashboard(input: $input) {
      errors
      result {
        id
        updatedAt
      }
    }
  }
`;

export const CreateCropVarietyMutation = gql`
  mutation CreateCropVariety($input: UpsertFarmCropInput!) {
    upsertFarmCrop(input: $input) {
      errors
      result {
        id
        updatedAt
        cropVarieties {
          id
          name
        }
      }
    }
  }
`;
